import { MkIcon } from "@/mk/MkIcon";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useWorkspace } from "@/api/workspace";
import { PM_SIDEBAR_DEFAULT, PM_SIDEBAR_MAX, PM_SIDEBAR_MIN, applySidebarOrder,
         readSidebarOrder, readSidebarWidth, writeSidebarOrder, writeSidebarWidth } from "@/app/sidebar";

import { MkAvatar } from "./MkAvatar";
import type { Brand, NavItem, Personal } from "./types";

interface Props {
    compact?: boolean;
    brand?: Brand;
    personal?: Personal;
    items: NavItem[];
    active?: string;
    onSelect(id: string, item: NavItem): void;
    onLogout?(): void;
    logoutLabel?: string;
    switcher?: ReactNode;
    /** the application the reader is in: entering another one opens its branch */
    section?: string;
}

function initial(item: NavItem): string {
    return (item.label || "?").trim().charAt(0).toUpperCase();
}

export function MkSidebar({ brand, personal, items, active,
                            onSelect, onLogout, logoutLabel = "Log out", switcher, section, compact = false }: Props) {
    const { t } = useTranslation();
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const [width, setWidth] = useState<number>(() => readSidebarWidth());
    const [expanded, setExpanded] = useState<number>(() => {
        const current = readSidebarWidth();
        return current > PM_SIDEBAR_MIN + 1 ? current : PM_SIDEBAR_DEFAULT;
    });
    const [resizing, setResizing] = useState(false);
    const [editing, setEditing] = useState(false);
    const mode = useWorkspace();
    const [order, setOrder] = useState<string[]>(() => readSidebarOrder(mode));
    const [dragId, setDragId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);
    const nav = useRef<HTMLElement>(null);
    const touchDrag = useRef<string | null>(null);

    const collapsed = !compact && width <= PM_SIDEBAR_MIN + 1;
    const ordered = useMemo(() => applySidebarOrder(items, order), [items, order]);
    /** Explicit menu editing temporarily shows the labels; hovering never changes its width. */
    const shownWidth = compact ? PM_SIDEBAR_DEFAULT : editing
        ? Math.max(expanded, PM_SIDEBAR_MIN + 1)
        : width;
    const iconMode = collapsed && !editing;

    // Each workspace has its own saved order: switching between design and
    // execution swaps the arrangement in, it does not mix the two.
    useEffect(() => {
        setOrder(readSidebarOrder(mode));
    }, [mode]);

    // Entering another application opens its branch as the menu first drew it,
    // without drawing the menu again: the reader's place in it is kept.
    useEffect(() => {
        setOpen({});
    }, [section]);

    // The open application stays in sight. The menu scrolls only as far as it
    // must to show the application and its pages, never back to the top.
    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            const box = nav.current;
            if (!box) {
                return;
            }
            const current = box.querySelector<HTMLElement>(".mk-sidebar-children-current");
            const head = (current?.previousElementSibling as HTMLElement | null)
                ?? box.querySelector<HTMLElement>(".mk-sidebar-item-active");
            if (!head) {
                return;
            }
            const frameTop = box.getBoundingClientRect().top;
            // A heading is not the page: the group it names has to stay in
            // sight too, or the reader sees entries with no name above them.
            const heading = (head.previousElementSibling as HTMLElement | null);
            const anchor = heading && heading.classList.contains("mk-sidebar-section")
                ? heading : null;
            const top = (anchor ?? head).getBoundingClientRect().top - frameTop + box.scrollTop - 8;
            const last = current ?? head;
            const bottom = last.getBoundingClientRect().bottom - frameTop + box.scrollTop + 8;
            if (top < box.scrollTop) {
                box.scrollTo({ top, behavior: "smooth" });
            } else if (bottom > box.scrollTop + box.clientHeight) {
                box.scrollTo({ top: Math.min(top, bottom - box.clientHeight), behavior: "smooth" });
            }
        });
        return () => cancelAnimationFrame(frame);
    // the items arrive after the first draw: the place is found again once they do
    }, [active, section, ordered]);

    function startResize(event: React.MouseEvent) {
        event.preventDefault();
        const startX = event.clientX;
        const startWidth = width;
        const rtl = document.dir === "rtl";
        setResizing(true);
        const move = (point: MouseEvent) => {
            const delta = rtl ? startX - point.clientX : point.clientX - startX;
            const next = Math.min(PM_SIDEBAR_MAX,
                                  Math.max(PM_SIDEBAR_MIN, Math.round(startWidth + delta)));
            setWidth(next);
        };
        const stop = (point: MouseEvent) => {
            const delta = rtl ? startX - point.clientX : point.clientX - startX;
            const next = Math.min(PM_SIDEBAR_MAX,
                                  Math.max(PM_SIDEBAR_MIN, Math.round(startWidth + delta)));
            // A nudge past the edge snaps shut to icons; anything wider stays a width.
            const snapped = next <= PM_SIDEBAR_MIN + 14 ? PM_SIDEBAR_MIN : next;
            setWidth(snapped);
            if (snapped > PM_SIDEBAR_MIN + 1) {
                setExpanded(snapped);
            }
            writeSidebarWidth(snapped);
            setResizing(false);
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", stop);
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", stop);
    }

    function moveItem(from: string, to: string) {
        const ids = ordered.filter((entry) => !entry.section && entry.id).map((entry) => entry.id as string);
        const fromIndex = ids.indexOf(from);
        const toIndex = ids.indexOf(to);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
            return;
        }
        const next = [...ids];
        const [moved] = next.splice(fromIndex, 1);
        if (moved === undefined) {
            return;
        }
        next.splice(toIndex, 0, moved);
        setOrder(next);
        writeSidebarOrder(next, mode);
    }

    const isActive = (id?: string) =>
        id != null && active != null && String(id) === String(active);

    const hasChildren = (item: NavItem) => Boolean(item.children?.length);
    const isOpen = (item: NavItem) => open[item.id] ?? Boolean(item.defaultOpen ?? true);

    function isBranchActive(item: NavItem): boolean {
        if (!hasChildren(item)) {
            return false;
        }
        return (item.children ?? []).some(
            (child) => isActive(child.id) || isBranchActive(child));
    }

    function branchClass(item: NavItem): string {
        if (!hasChildren(item)) {
            return "";
        }
        const classes: string[] = [];
        if (isOpen(item)) {
            classes.push("mk-sidebar-item-open");
        }
        if (isBranchActive(item)) {
            classes.push("mk-sidebar-item-branch");
        }
        return classes.length ? ` ${classes.join(" ")}` : "";
    }

    function toggle(item: NavItem) {
        setOpen((current) => ({ ...current, [item.id]: !isOpen(item) }));
    }

    function click(item: NavItem) {
        // While the order is being rearranged, taps only open branches: nothing navigates.
        if (editing) {
            if (hasChildren(item)) {
                toggle(item);
            }
            return;
        }
        if (hasChildren(item) && !iconMode) {
            if (!isOpen(item)) {
                setOpen((current) => ({ ...current, [item.id]: true }));
                return;
            }
            if (!item.selectable) {
                toggle(item);
                return;
            }
        }
        onSelect(item.id, item);
    }

    function renderItems(list: NavItem[], depth: number) {
        return list.map((item, index) => {
            if (item.section) {
                return (
                    <div className="mk-sidebar-section" key={item.id || `s${index}`}>
                        {item.section}
                    </div>
                );
            }
            const draggable = editing && depth === 0;
            return (
                <Fragment key={item.id || `s${index}`}>
                    <button
                        className={(depth ? "mk-sidebar-child" : "mk-sidebar-item")
                            + (isActive(item.id) ? " mk-sidebar-item-active" : "")
                            + branchClass(item)
                            + (dragId === item.id ? " mk-sidebar-item-dragging" : "")
                            + (overId === item.id && dragId !== item.id ? " mk-sidebar-item-droptarget" : "")}
                        title={editing && depth === 0 ? t("mk.sidebar.dragHint")
                            : item.suffix ? `${item.label} (${item.suffix})` : item.label}
                        onClick={() => click(item)}
                        draggable={draggable}
                        data-sidebar-id={depth === 0 ? item.id : undefined}
                        onDragStart={(event) => {
                            if (!draggable) {
                                return;
                            }
                            event.dataTransfer.effectAllowed = "move";
                            try {
                                event.dataTransfer.setData("text/plain", item.id);
                            } catch {
                                return;
                            }
                            setDragId(item.id);
                        }}
                        onDragOver={(event) => {
                            if (!draggable || !dragId || dragId === item.id) {
                                return;
                            }
                            event.preventDefault();
                            event.dataTransfer.dropEffect = "move";
                            setOverId(item.id);
                        }}
                        onDragLeave={() => {
                            if (overId === item.id) {
                                setOverId(null);
                            }
                        }}
                        onDrop={(event) => {
                            if (!draggable || !dragId || !item.id) {
                                return;
                            }
                            event.preventDefault();
                            moveItem(dragId, item.id);
                            setDragId(null);
                            setOverId(null);
                        }}
                        onDragEnd={() => {
                            setDragId(null);
                            setOverId(null);
                        }}
                    >
                        {editing && depth === 0 && (
                            <span className="mk-sidebar-grip" aria-hidden="true"
                                  onClick={(event) => event.stopPropagation()}
                                  onPointerDown={(event) => {
                                      if (event.pointerType !== "touch") return;
                                      event.preventDefault(); event.stopPropagation();
                                      event.currentTarget.setPointerCapture(event.pointerId);
                                      touchDrag.current = item.id; setDragId(item.id);
                                  }}
                                  onPointerMove={(event) => {
                                      if (!touchDrag.current) return;
                                      const target = document.elementFromPoint(event.clientX, event.clientY)
                                          ?.closest<HTMLElement>("[data-sidebar-id]");
                                      setOverId(target?.dataset.sidebarId || null);
                                  }}
                                  onPointerUp={(event) => {
                                      if (!touchDrag.current) return;
                                      const target = document.elementFromPoint(event.clientX, event.clientY)
                                          ?.closest<HTMLElement>("[data-sidebar-id]")?.dataset.sidebarId;
                                      if (target && target !== touchDrag.current) moveItem(touchDrag.current, target);
                                      touchDrag.current = null; setDragId(null); setOverId(null);
                                  }}
                                  onPointerCancel={() => { touchDrag.current = null; setDragId(null); setOverId(null); }}>
                                <MkIcon name="fa-bars" />
                            </span>
                        )}
                        {depth && !item.icon ? (
                            <>
                                <span className="mk-sidebar-dot" />
                                <span className="mk-sidebar-initial">{initial(item)}</span>
                            </>
                        ) : (
                            <span className="mk-sidebar-item-icon">
                                <MkIcon name={item.icon || "fa-circle-o"} />
                            </span>
                        )}
                        <span className="mk-sidebar-item-label">{item.label}</span>
                        {item.suffix ? (
                            <span className="mk-sidebar-item-suffix" dir="ltr">({item.suffix})</span>
                        ) : null}
                        {item.badge ? (
                            <span className="mk-sidebar-badge">{item.badge}</span>
                        ) : null}
                        {hasChildren(item) && (
                            <span
                                className="mk-sidebar-caretbtn"
                                role="button"
                                tabIndex={0}
                                title={isOpen(item) ? t("mk.collapse") : t("mk.expand")}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggle(item);
                                }}
                            >
                                <MkIcon name="fa-angle-down" className={isOpen(item) ? "mk-open" : ""} />
                            </span>
                        )}
                    </button>
                    {hasChildren(item) && isOpen(item) && (
                        <div className={`mk-sidebar-children${
                            isBranchActive(item) ? " mk-sidebar-children-current" : ""}`}>
                            {renderItems(item.children ?? [], depth + 1)}
                        </div>
                    )}
                </Fragment>
            );
        });
    }

    return (
        <aside className={`mk-sidebar${iconMode ? " mk-sidebar-icon" : ""}${
                        resizing ? " mk-sidebar-resizing" : ""}`}
               style={{ width: shownWidth }}>
            <nav className="mk-sidebar-nav" ref={nav}>
                {brand && (
                    <div className="mk-sidebar-brandbar">
                        <button
                            className={`mk-sidebar-item mk-sidebar-brand${
                                isActive(brand.id) ? " mk-sidebar-item-active" : ""}`}
                            title={brand.label}
                            onClick={() => brand.onClick?.()}
                        >
                            <span className="mk-sidebar-item-icon mk-sidebar-brand-icon">
                                {brand.logo
                                    ? <img src={brand.logo} alt="" />
                                    : <MkIcon name={brand.icon || "fa-cube"} />}
                            </span>
                            <span className="mk-sidebar-item-label">{brand.label || "ProMaster"}</span>
                        </button>
                        <button className={`mk-sidebar-edit${editing ? " mk-sidebar-edit-active" : ""}`}
                                onClick={() => setEditing((value) => !value)}
                                title={editing ? t("mk.sidebar.done") : t("mk.sidebar.editOrder")}>
                            <MkIcon name={editing ? "fa-check" : "fa-pencil"} />
                        </button>
                    </div>
                )}

                {switcher}

                {renderItems(ordered, 0)}
            </nav>

            {(personal || onLogout) && (
                <div className="mk-sidebar-footer">
                    {personal && (
                        <button className="mk-sidebar-user" title={personal.name}
                                onClick={() => personal.onClick?.()}>
                            <MkAvatar size="sm" src={personal.avatarUrl}
                                      name={personal.name} status={personal.status} />
                            <span className="mk-sidebar-user-name">{personal.name}</span>
                        </button>
                    )}
                    {onLogout && (
                        <button className="mk-sidebar-logout" title={logoutLabel}
                                onClick={() => onLogout()}>
                            <MkIcon name="fa-sign-out" />
                        </button>
                    )}
                </div>
            )}
            <div className="mk-sidebar-resizer" title={t("mk.sidebar.resize")}
                 onMouseDown={startResize} />
        </aside>
    );
}
